// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import ProductDescription, { ProductDescriptionStyles } from "@store/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import openMinicartDrawer from "@store/Framework/Store/Store/Components/MinicartDrawer/Handlers/openMinicartDrawer";
import closePartFinderModal from "@store/Framework/Store/Store/Components/PartFinder/Handlers/ClosePartFinderModal";
import { ProductAndQuantityModel } from "@store/Framework/Types/ApiModels";
import ProductAvailabilityWithPickup, {
    ProductAvailabilityWithPickupStyles,
} from "@store/Library/Components/ProductAvailabilityWithPickup";
import SupersededByComponent from "@store/Library/Components/ProductDetails/SupersededBy";
import ProductBrand, { ProductBrandStyles } from "@store/Overrides/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { partFinderProps } from "@store/Themes/common/props/partFinder";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productToAddToModal?: ProductAndQuantityModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    partFinderModalOpen: state.store.components.partFinder.partFinderModalOpen,
    productQty: state.store.components.partFinder.productQty,
    productToAddToModal: state.store.components.partFinder.productToAddToModal,
});

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    closePartFinderModal,
    openMinicartDrawer,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const gridItemProps: GridItemProps = {
    width: [12, 12, 6, 6, 6],
    printWidth: 6,
    css: css`
        display: block;
    `,
};

const contentSpacing = pxToRem(10);

export interface PartFinderModalStyles {
    partModal?: ModalPresentationProps;
    productGridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partQtyInUnit?: TypographyPresentationProps;
    productPriceStyles?: ProductPriceStyles;
    addToCartOptionsAndButtonWrapper?: FlexWrapperPresentationProps;
    productQtyFormField?: TextFieldPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    productAvailabilityWithPickupStyles?: ProductAvailabilityWithPickupStyles;
    obsoleteWrapper?: InjectableCss;
}

export const partFinderModalStyles: PartFinderModalStyles = {
    partModal: {
        size: 950,
        cssOverrides: {
            modalBody: css`
                background-color: ${getColor("common.accent")};
            `,
        },
    },
    productGridContainer: { gap: 0 },
    productImageGridItem: gridItemProps,
    productImage: {
        linkWrappingImage: {
            css: css`
                max-height: ${pxToRem(300)};

                ${breakpoint("md")} {
                    max-height: ${pxToRem(500)};
                }
            `,
        },
        wrapper: {
            css: css`
                background-color: ${getColor("common.background")};
            `,
        },
    },
    productInfoGridItem: mergeProps<PartFinderModalStyles["productInfoGridItem"]>(gridItemProps, {
        css: css`
            padding-top: ${pxToRem(15)};

            ${breakpoint("md")} {
                padding-left: ${pxToRem(26)};
                padding-top: 0;
            }
        `,
    }),
    productDescriptionStyles: {
        wrapper: {
            forwardAs: "h3",
        },
        productDetailDescription: {
            css: css`
                ${breakpointValues(pxToRemArray([22, null, null, 26, null]), "font-size")}
            `,
        },
    },
    productPartNumbersStyles: {
        wrapper: {
            css: css`
                ${({ theme }) => css`
                    ${spacingStyles(null, contentSpacing)}
                    line-height: ${theme.design.typography.lineHeight.base};
                `}
                display: flex;
                flex-wrap: wrap;
            `,
        },
        innerWrapper: {
            css: css`
                ${inlineSeparatorStyles({ spacing: "1em", width: pxToRem(1), color: "text.accent" })}
                margin-bottom: 0;
            `,
        },
    },
    partQtyInUnit: mergeProps<PartFinderModalStyles["partQtyInUnit"]>(partFinderProps.partQtyInUnit, {
        css: css`
            ${spacingStyles(null, contentSpacing)}
        `,
    }),
    productPriceStyles: {
        wrapper: {
            css: css`
                ${spacingStyles(contentSpacing, contentSpacing)}
            `,
        },
        price: {
            priceText: {
                css: css`
                    font-size: ${pxToRem(35)};
                `,
            },
        },
    },
    addToCartOptionsAndButtonWrapper: {
        gap: pxToRem(10),
        extendedStyles: {
            wrapper: {
                css: css`
                    ${spacingStyles(null, contentSpacing)}
                `,
            },
        },
    },
    productQtyFormField: partFinderProps.productQtyFormField,
    addToCartButton: partFinderProps.addToCartButton,
    productAvailabilityWithPickupStyles: {
        container: {
            css: css`
                ${spacingStyles(pxToRem(30), null)}
                padding: 0;
            `,
        },
    },
    obsoleteWrapper: {
        css: css`
            color: ${getColor("warning.main")};
            border: 1px solid ${getColor("warning.main")};
            padding: ${pxToRem(14)};
            margin-bottom: 1em;
        `,
    },
};

const styles = partFinderModalStyles;

const PartFinderModal = ({
    productToAddToModal,
    addToCart,
    partFinderModalOpen,
    productQty,
    closePartFinderModal,
    openMinicartDrawer,
}: Props) => {
    const [orderQty, setOrderQty] = useState<string | undefined>(
        productQty ? (productQty === "0" ? "1" : productQty) : "1",
    );
    const [isLoading, setIsLoading] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const productContext = React.useContext(ProductContext);
    const product = productContext.product;
    const isObsolete = !!(
        product &&
        product.properties?.isObsolete &&
        product.properties?.isObsolete?.toLowerCase() === "true"
    );
    const isSupersededBy = product && product.properties.supersededBy;
    const trackAddToCart = ga.use.trackAddToCart();

    useEffect(() => {
        if (partFinderModalOpen) {
            setOrderQty(productQty || "1");
        } else {
            setOrderQty("0");
        }
    }, [partFinderModalOpen]);

    const modalCloseHandler = () => {
        closePartFinderModal();
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            // ENTER_KEY
            (event.target as any).blur();
        }
    };

    const addToCartClickHandler = async () => {
        setIsLoading(true);
        if (productToAddToModal && orderQty) {
            let unitOfMeasure = "";
            if (product.unitOfMeasures) {
                unitOfMeasure = product.unitOfMeasures[0].unitOfMeasure;
            }

            const cartline = (await addToCart({
                productId: product?.id,
                qtyOrdered: parseInt(orderQty, 10),
                unitOfMeasure,
                onError: () => {
                    setIsLoading(false);
                    toasterContext.addToast({
                        body: translate("Cannot add product to cart. Please try again in a few moments."),
                        messageType: "danger",
                    });
                },
            })) as CartLineModel;
            trackAddToCart();

            if (cartline) {
                closePartFinderModal();
                openMinicartDrawer();
            }
            setIsLoading(false);
        }
    };

    if (!productToAddToModal) {
        return null;
    }

    return (
        <Modal {...styles.partModal} isOpen={partFinderModalOpen} handleClose={modalCloseHandler}>
            <GridContainer {...styles.productGridContainer}>
                <GridItem {...styles.productImageGridItem}>
                    <ProductImage product={product as any} size="medium" extendedStyles={styles.productImage} />
                </GridItem>
                <GridItem {...styles.productInfoGridItem}>
                    {product.brand && <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />}
                    <ProductDescription product={product} extendedStyles={styles.productDescriptionStyles} />
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    <Typography {...styles.partQtyInUnit}>{`${translate("Qty in Unit:")} ${
                        productToAddToModal.qtyInUnit || 1
                    }`}</Typography>

                    {isObsolete && ( // Store Custom Code
                        <StyledWrapper {...styles.obsoleteWrapper}>
                            <Typography data-test-selector="productDetails_obsoleteWarning">
                                <span>{translate("Store_ProductDetail_Obsolete_Warning")}</span>
                            </Typography>
                        </StyledWrapper>
                    )}
                    {product && <SupersededByComponent product={product} />}
                    {!isSupersededBy && !isObsolete && (
                        <>
                            <ProductAvailabilityWithPickup
                                isProductDetailsPage={false}
                                isScratchAndDent={product.properties?.isScratchAndDent}
                                dcneAvailability={product.properties?.availabilityStatus}
                                canAddToCart={product.canAddToCart}
                            />
                            {productContext.productInfo ? (
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showSavings={true}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            ) : null}
                            <FlexWrapper {...styles.addToCartOptionsAndButtonWrapper}>
                                <TextField
                                    label={translate("QTY")}
                                    value={orderQty}
                                    type="number"
                                    min={0}
                                    max={999999}
                                    data-test-selector="product_qtyOrdered"
                                    onKeyPress={keyPressHandler}
                                    onChange={event => {
                                        setOrderQty(event.target.value.toString());
                                    }}
                                    {...styles.productQtyFormField}
                                />
                                <Button
                                    {...styles.addToCartButton}
                                    onClick={addToCartClickHandler}
                                    disabled={isLoading}
                                    isLoading={isLoading}
                                >
                                    {translate("Add to Cart")}
                                </Button>
                            </FlexWrapper>
                            <ProductAvailabilityWithPickup
                                isProductDetailsPage={true}
                                isScratchAndDent={product.properties?.isScratchAndDent}
                                dcneAvailability={product.properties?.availabilityStatus}
                                canAddToCart={product.canAddToCart}
                                extendedStyles={styles.productAvailabilityWithPickupStyles}
                            />
                        </>
                    )}
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PartFinderModal);
